import React, { useEffect, useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { getAllBudgetLines } from '../../clients/budget_line_client';
import { formatBudgetLineLabel } from '../../utils/labData';
import { logError, logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'BudgetLineSelect';

const cache = {
  data: null,
  timestamp: 0,
  isFetching: false,
  promise: null,
};

const CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Searchable budget line selector for quote/project funding.
 */
const BudgetLineSelect = ({
  token,
  value,
  onChange,
  disabled = false,
  error = '',
  label = 'Budget',
  required = false,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setOptions([]);
      return;
    }

    const now = Date.now();
    if (cache.data && now - cache.timestamp < CACHE_TTL_MS) {
      setOptions(cache.data);
      return;
    }

    if (cache.isFetching && cache.promise) {
      cache.promise.then(() => {
        if (cache.data) setOptions(cache.data);
      });
      return;
    }

    setLoading(true);
    cache.isFetching = true;
    logInfo(LOG_CONTEXT, 'load budget lines');

    cache.promise = getAllBudgetLines(token, { isActive: true }).then(result => {
      cache.isFetching = false;
      setLoading(false);
      if (result.success) {
        cache.data = result.data;
        cache.timestamp = Date.now();
        setOptions(result.data);
      } else {
        logError(LOG_CONTEXT, 'load failed', { messages: result.messages });
      }
    });
  }, [token]);

  const selectedOption = useMemo(() => {
    if (value == null || value === '') return null;
    return options.find(o => String(o.id) === String(value)) ?? null;
  }, [options, value]);

  return (
    <Autocomplete
      options={options}
      loading={loading}
      disabled={disabled}
      value={selectedOption}
      onChange={(_, option) => onChange(option?.id ?? null)}
      getOptionLabel={option => formatBudgetLineLabel(option)}
      isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={Boolean(error)}
          helperText={error || 'Select an active budget'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          {formatBudgetLineLabel(option)}
        </Box>
      )}
    />
  );
};

/** Invalidate cache after Budget CRUD on Lab Data pages. */
export function invalidateBudgetLineSelectCache() {
  cache.data = null;
  cache.timestamp = 0;
}

export default BudgetLineSelect;
